import { useEffect } from 'react';

const SITE_NAME = 'Whe2';
const SITE_URL = 'https://www.whe2.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_TWITTER_IMAGE = `${SITE_URL}/twitter-card.png`;
const DEFAULT_TITLE = 'Whe2 | South Africa Route Planner, Fuel Cost and Toll Calculator';
const DEFAULT_DESCRIPTION = 'Plan South African road trips with route comparison, fuel cost estimates and toll calculations.';

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  return element;
};

const ensureLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  return element;
};

const upsertJsonLd = (id, data) => {
  const selector = `script[data-seo-jsonld="${id}"]`;
  if (!data) {
    const existing = document.head.querySelector(selector);
    if (existing) existing.remove();
    return;
  }

  let script = document.head.querySelector(selector);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

const toAbsoluteUrl = (value = '/') => {
  try {
    return new URL(value, SITE_URL).toString();
  } catch {
    return SITE_URL;
  }
};

const Seo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  twitterImage = DEFAULT_TWITTER_IMAGE,
  robots = 'index,follow',
  type = 'website',
  jsonLd,
}) => {
  useEffect(() => {
    const absoluteUrl = toAbsoluteUrl(path);
    const absoluteImage = toAbsoluteUrl(image);
    const absoluteTwitterImage = toAbsoluteUrl(twitterImage);

    document.title = title;

    ensureMeta('meta[name="description"]', { name: 'description', content: description });
    ensureMeta('meta[name="robots"]', { name: 'robots', content: robots });
    ensureLink('link[rel="canonical"]', { rel: 'canonical', href: absoluteUrl });

    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: absoluteUrl });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: absoluteImage });

    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: absoluteTwitterImage });

    upsertJsonLd('primary', jsonLd);

    return () => {
      upsertJsonLd('primary', null);
    };
  }, [description, image, jsonLd, path, robots, title, twitterImage, type]);

  return null;
};

export default Seo;
